import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import DSBox from "views/system-design/DSBox";
import DSTooltip from "views/system-design/DSTooltip";

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

const hexEntries = (json: Record<string, unknown> | undefined) =>
  Object.entries(json ?? {}).filter(
    (entry): entry is [string, string] =>
      typeof entry[1] === "string" && HEX_COLOR.test(entry[1]),
  );

/**
 * The theme colors of a finished klydo animation. Uses the colors stored on
 * the job when present; otherwise reads the JSON output, and shows nothing if
 * the file cannot be read (for example, without a bucket CORS rule).
 */
const ThemeColorSwatches = ({
  url,
  theme,
}: {
  url: string;
  theme?: Record<string, string>;
}) => {
  const { data: fetched } = useQuery({
    queryKey: ["creative-theme-colors", url],
    queryFn: async () => {
      const { data: json } = await axios.get<Record<string, unknown>>(url);
      return hexEntries(json);
    },
    staleTime: Infinity,
    retry: false,
    enabled: !theme,
  });
  const data = theme ? hexEntries(theme) : fetched;

  if (!data?.length) return null;

  return (
    <DSBox sx={{ display: "flex", gap: 0.5 }}>
      {data.map(([name, color]) => (
        <DSTooltip key={name} title={`${name}: ${color}`}>
          <DSBox
            sx={{
              width: 16,
              height: 16,
              borderRadius: "4px",
              backgroundColor: color,
              border: "1px solid",
              borderColor: "divider",
            }}
          />
        </DSTooltip>
      ))}
    </DSBox>
  );
};

export default ThemeColorSwatches;
